import type {
  ErosionInvestigationConfig,
  ErosionObservationResult,
  ErosionTestConditionId,
} from './ErosionInvestigationConfig.js';

export type ErosionInvestigationPhase =
  | 'observe'
  | 'predict'
  | 'test'
  | 'compare'
  | 'complete';

export interface ErosionTestRecord {
  readonly conditionId: ErosionTestConditionId;
  readonly result: ErosionObservationResult;
  readonly runNumber: number;
}

export interface ErosionComparisonResult {
  readonly predictionSupported: boolean;
  readonly evidenceStatement: string;
}

export interface ErosionInvestigationSnapshot {
  readonly investigationId: string;
  readonly phase: ErosionInvestigationPhase;
  readonly predictionId?: string;
  readonly selectedConditionId?: ErosionTestConditionId;
  readonly testRecords: readonly ErosionTestRecord[];
  readonly comparison?: ErosionComparisonResult;
}

export class ErosionInvestigationState {
  private phaseValue: ErosionInvestigationPhase = 'observe';
  private predictionIdValue?: string;
  private selectedConditionIdValue?: ErosionTestConditionId;
  private readonly records = new Map<ErosionTestConditionId, ErosionTestRecord>();
  private comparisonValue?: ErosionComparisonResult;

  constructor(readonly config: ErosionInvestigationConfig) {}

  get phase() { return this.phaseValue; }
  get isComplete() { return this.phaseValue === 'complete'; }
  get readyToCompare() {
    return this.config.testConfig.conditions.every(condition => this.records.has(condition.id));
  }

  snapshot(): ErosionInvestigationSnapshot {
    return {
      investigationId: this.config.id,
      phase: this.phaseValue,
      predictionId: this.predictionIdValue,
      selectedConditionId: this.selectedConditionIdValue,
      testRecords: [...this.records.values()].sort((a, b) => a.runNumber - b.runNumber),
      comparison: this.comparisonValue,
    };
  }

  acknowledgeObservation() {
    if (this.phaseValue !== 'observe') return false;
    this.phaseValue = 'predict';
    return true;
  }

  choosePrediction(predictionId: string) {
    if (this.phaseValue !== 'predict') return false;
    if (!this.config.predictionOptions.some(option => option.id === predictionId)) return false;
    this.predictionIdValue = predictionId;
    this.phaseValue = 'test';
    return true;
  }

  selectCondition(conditionId: ErosionTestConditionId) {
    if (this.phaseValue !== 'test') return false;
    if (!this.config.testConfig.conditions.some(condition => condition.id === conditionId)) return false;
    if (this.records.has(conditionId)) return false;
    this.selectedConditionIdValue = conditionId;
    return true;
  }

  runSelectedCondition(): ErosionTestRecord | undefined {
    if (this.phaseValue !== 'test' || !this.selectedConditionIdValue) return undefined;
    const conditionId = this.selectedConditionIdValue;
    const record: ErosionTestRecord = {
      conditionId,
      result: this.config.observationResults[conditionId],
      runNumber: this.records.size + 1,
    };
    this.records.set(conditionId, record);
    this.selectedConditionIdValue = undefined;
    if (this.readyToCompare) this.phaseValue = 'compare';
    return record;
  }

  compareResults(): ErosionComparisonResult | undefined {
    if (this.phaseValue !== 'compare' || !this.predictionIdValue) return undefined;
    this.comparisonValue = {
      predictionSupported: this.predictionIdValue === this.config.supportedPredictionId,
      evidenceStatement: this.config.evidenceStatement,
    };
    this.phaseValue = 'complete';
    return this.comparisonValue;
  }

  reset() {
    this.phaseValue = 'observe';
    this.predictionIdValue = undefined;
    this.selectedConditionIdValue = undefined;
    this.records.clear();
    this.comparisonValue = undefined;
  }
}
